import { AllDesiresId } from '~features/gridFilters';
import type {
	GetGridApiArgs,
	GetGridApiResponse,
	TGridFiltersResponse,
} from '~services/api/grid/types';
import { getGridSerializeQueryArgs } from '~services/api/grid/utils';
import { api, apiTags } from '~shared/api/origin';
import type { RootState } from '~store/index';
import { removeDuplicates } from '~utils/array';
import indexDb from '~utils/indexDb';

export const gridApi = api.injectEndpoints({
	endpoints: (build) => ({
		getGridFilters: build.query<TGridFiltersResponse, {}>({
			query: () => ({ url: `/ai/chats/search/filters` }),
			transformResponse: (data: TGridFiltersResponse) => {
				const transformedResponse = data.sort(
					(left, right) => left.order - right.order
				);
				transformedResponse.unshift({
					id: AllDesiresId,
					order: 0,
					filter: 'All',
				});
				return transformedResponse;
			},
		}),
		getGrid: build.query<GetGridApiResponse, GetGridApiArgs>({
			// @ts-ignore
			queryFn: async (arg, queryApi, extraOptions, baseQuery) => {
				const isDemoUser = (
					queryApi.getState() as RootState
				).auth.info.roles.includes('demo');
				const key = `${getGridSerializeQueryArgs('getGrid', arg)}-${
					isDemoUser ? 'true' : 'false'
				}`;
				const cachedData = (await indexDb.getCache(key)) as GetGridApiResponse;
				if (cachedData) {
					queryApi.dispatch(
						gridApi.util.upsertQueryData('getGrid', arg, cachedData)
					);
				}

				try {
					const response = await baseQuery({
						url: `/ai/grid/${arg.userId}`,
						params: {
							omit: arg.omit,
							select: arg.select,
							q: arg.keyword,
							botTypesMode: arg.createdBy,
							gen: arg.gender,
							desires: arg.desire,
							ageMin: arg.minAge,
							ageMax: arg.maxAge,
						},
					});

					if (response.data) {
						void indexDb.putCache(key, response.data);
					}

					return response;
				} catch (error) {
					return { error };
				}
			},
			forceRefetch({ currentArg, endpointState }) {
				return (
					// @ts-ignore
					// eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
					(currentArg?.omit ?? 0) >= (endpointState?.data?.botGrid?.length ?? 0)
				);
			},
			merge: (
				currentState: GetGridApiResponse,
				incomingState: GetGridApiResponse
			) => {
				currentState.hasMoreRows = incomingState.hasMoreRows;
				currentState.botGrid.push(...incomingState.botGrid);
				currentState.botGrid = removeDuplicates(
					[...currentState.botGrid, ...incomingState.botGrid],
					'uid'
				);
			},
			serializeQueryArgs: ({ endpointName, queryArgs }) => {
				const { omit: _, ...rest } = queryArgs;
				return getGridSerializeQueryArgs(endpointName, rest);
			},
			onCacheEntryAdded(
				arg,
				{ cacheDataLoaded, getCacheEntry, cacheEntryRemoved, getState }
			) {
				const isDemoUser = (getState() as RootState).auth.info.roles.includes(
					'demo'
				);
				const key = `${getGridSerializeQueryArgs('getGrid', arg)}-${
					isDemoUser ? 'true' : 'false'
				}`;
				void cacheDataLoaded.then(() => {
					const cacheEntry = getCacheEntry();

					if (cacheEntry?.data) {
						void indexDb.putCache(key, cacheEntry.data);
					}
				});

				void cacheEntryRemoved.then(() => {
					void indexDb.deleteCache(key);
				});
			},
			providesTags: [apiTags.grid],
		}),
	}),
});
